import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/software_and_services_agreement")({
  head: () => ({
    meta: [
      { title: "Software License and Services Terms — SaaSed.co" },
      { property: "og:title", content: "Software License and Services Terms — SaaSed.co" },
      { property: "og:description", content: "SaaSed.co Web-Based Software License and Services Terms for the More Certainty app." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      {
        name: "description",
        content:
          "SaaSed.co Web-Based Software License and Services Terms for the More Certainty app.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
    links: [{ rel: "canonical", href: "https://saased.co/software_and_services_agreement" }],
  }),
  component: SoftwareAgreementPage,
});

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-10">
      <h2 className="font-serif text-2xl text-foreground md:text-3xl">{title}</h2>
      <div className="mt-4 space-y-4 text-[15px] leading-relaxed text-foreground/80">
        {children}
      </div>
    </section>
  );
}

function SoftwareAgreementPage() {
  return (
    <div className="bg-background">
      <div className="container-x py-16 md:py-24">
        <article className="mx-auto max-w-3xl rounded-3xl border border-border/60 bg-card p-8 shadow-soft md:p-14">
          <header>
            <p className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
              Legal
            </p>
            <h1 className="mt-3 font-serif text-4xl leading-tight text-foreground md:text-5xl">
              SaaSed.co Software License and Services Terms
            </h1>
            <p className="mt-4 text-sm text-muted-foreground">
              Effective Date: 15/10/2025
            </p>
          </header>

          <div className="mt-8 space-y-4 text-[15px] leading-relaxed text-foreground/80">
            <p>
              These SaaSed.co Web-Based Software License and Services Terms (the
              &ldquo;Terms&rdquo;), together with an Order Form, and any and all
              Exhibits and Attachments form the &ldquo;Agreement&rdquo; for the
              licensing of the &ldquo;More Certainty&rdquo; app. The Agreement
              enters into effect with the execution of one or more More
              Certainty Order Forms. The Agreement applies between SaaSed.co Limited
              (&ldquo;Provider&rdquo;) and the person, company, or other entity
              entering into the Order Form or similar agreement hereunder (the
              &ldquo;Licensee&rdquo;). Terms not defined here have the meanings
              given to them in the Order Form.
            </p>
          </div>

          <Section title="1. Definitions">
            <p>
              For the purposes of the Agreement, the following terms have these
              definitions:
            </p>
            <ul className="list-disc space-y-3 pl-6">
              <li>
                <strong>AppExchange</strong> is the online directory of on-demand
                applications that work with the SFDC Platform, located at{" "}
                <a
                  href="http://www.appexchange.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 hover:text-foreground"
                >
                  http://www.appexchange.com
                </a>{" "}
                or at any successor websites.
              </li>
              <li>
                <strong>Documentation</strong> includes any user manuals,
                reference guides, specifications, documentation, instruction
                materials and similar recorded data and information, whether in
                electronic or physical output form, that the Provider provides
                or makes available to the Licensee or that the Provider makes
                publicly available, which may describe how the Software is to be
                operated by users.
              </li>
              <li>
                <strong>Intellectual Property Rights</strong> include all patent
                rights, copyright rights, mask work rights, moral rights, rights
                of publicity, trademark, trade dress and service mark rights,
                goodwill, trade secret rights and other intellectual property
                rights as may now exist or hereafter come into existence, and
                all applications thereto and registrations, renewals and
                extensions thereof, under the Laws of any state, country,
                territory or other jurisdiction.
              </li>
              <li>
                <strong>Laws</strong> include all existing and future relevant
                national, provincial, regional, territorial and local laws,
                international treaties, statutes, statutory instruments,
                ordinances, regulations, rules, executive orders, supervisory
                requirements, directives, circulars, opinions, interpretive
                letters and other office releases, guidelines, and policies
                with the force of law, of or by any government, or any
                governmental authority, department, or agency thereof
                (including banking laws, regulations, guidance, and policies),
                or any court of competent jurisdiction.
              </li>
              <li>
                <strong>Licensed Materials</strong> refers collectively to the
                Documentation and the Software.
              </li>
              <li>
                <strong>Licensee Inputs</strong> refers to any text, images,
                profile information, data, video, audiovisual content, works of
                authorship or other types of materials, information or
                communications, or hyperlinks to any of the foregoing, that the
                Licensee provides, imports, posts, uploads, publishes, transmits
                or distributes on or through the Licensed Materials.
              </li>
              <li>
                <strong>Org, Sandbox Org or Production Org</strong> refers to
                the virtual space (instance) provided by SFDC to the Licensee
                that includes Licensee Salesforce data, customizations and
                applications and is used in connection with the Services. An
                Org may make reference to the Licensee&rsquo;s production
                environment (&ldquo;Production Org&rdquo;) or a developer or
                test instance (&ldquo;Sandbox Org&rdquo;).
              </li>
              <li>
                <strong>Party / Parties</strong> refers to the Provider and the
                Licensee, jointly referred to as the &ldquo;Parties&rdquo;, and
                individually as a &ldquo;Party&rdquo;.
              </li>
              <li>
                <strong>Provider</strong> refers to SaaSed.co Limited, a private
                company with limited liability under Cyprus law, A Cypriot
                registered company &ndash; HE482282, registered address: 14
                Lapithou Street Office 014 Egkomi, 2410 Nicosia Cyprus.
              </li>
              <li>
                <strong>Services</strong> refers to the Support Services and
                any other services provided to the Licensee by the Provider.
              </li>
              <li>
                <strong>SFDC</strong> refers to Salesforce.com, Inc., a Delaware
                corporation, its services, and related entities as applicable,
                including its successors and assignees. Salesforce.com provides
                cloud-based software solutions and customer relationship
                management (CRM) services.
              </li>
              <li>
                <strong>SFDC Platform</strong> refers to the web-based
                technology platform provided by Salesforce that includes a user
                interface, operating system, customization and integration
                capabilities for Salesforce.com&rsquo;s on-demand customer
                relationship management service.
              </li>
              <li>
                <strong>Software</strong> refers to the online, web-based
                application More Certainty, provided by SaaSed.co via the
                AppExchange and/or other designated websites described on the
                Order Form.
              </li>
              <li>
                <strong>User</strong> refers to any individual accessing and/or
                using the Licensed Materials through the Licensee&rsquo;s
                Account.
              </li>
            </ul>
          </Section>

          <Section title="2. License and User Restrictions">
            <p>
              The Licensee may use the licensed Software solely for the
              Licensee&rsquo;s internal business purposes. The More Certainty
              app is licensed for use and not sold, consequently the license
              confers no title or ownership in this Software to the licensee
              and should not be constructed as a sale of any rights in this
              Software or source code. The Licensee may access and use the
              Software with the number of users defined in the Order Form.
            </p>
            <p>Ownership rights as between the Provider and the Licensee:</p>
            <p>
              (a) The Licensee owns all right, title and interest in and to (i)
              the Licensee Inputs; and (ii) any and all Intellectual Property
              Rights in and to the Licensee Inputs; and;
            </p>
            <p>
              (b) The Provider owns all right, title and interest in and to (i)
              the Licensed Product and Materials; and (ii) any and all
              Intellectual Property Rights in and to the Licensed Product and
              Materials.
            </p>
            <p>
              It is expressly forbidden for the Licensee to: Reverse engineer
              the source code of the software or to decompile the software,
              except to the extent permitted by mandatory law; Provide copies
              of the software to third parties; Sublicense the software or
              otherwise make the software available to third parties, including
              by rental, Software-as-a-Service models or otherwise; Modify the
              software, except to the extent permitted by mandatory law; or
              Remove indications of the Provider as the copyright holder of the
              software or to remove or render illegible any part thereof.
            </p>
            <p>
              In the event of any damage or harm outside the control of the
              Provider, such as caused by the SFDC-systems and SFDC-applications,
              the Provider has no liability. The Provider cannot guarantee the
              availability of the SFDC-systems and SFDC-applications.
            </p>
          </Section>

          <Section title="3. Services">
            <p>
              <strong>Support:</strong> During the Subscription Term, the
              Provider will provide a reasonable level of support through its
              website http://SaaSed.co and/or other communication channels
              announced to Licensee. The Provider however does not guarantee
              that all requests for support or bug reports will be resolved.
              The Licensee is solely responsible for the installation,
              configuration and usage of the software.
            </p>
          </Section>

          <Section title="4. Fees">
            <p>
              <strong>Fees and Payment.</strong> The Licensee shall pay the
              Provider the fees set forth on each applicable Order Form
              (&ldquo;Fees&rdquo;). The Fees are payable within fifteen (15)
              days from the date of invoice from the Provider, which shall be
              delivered in accordance with the schedule set forth in the Order
              Form. All Fees and invoices are to be paid in Euros (EUR). Unless
              otherwise specified in your Order Confirmation, payment is due
              upfront in a single lump sum and is invoiced at the start of your
              subscription (first-time customers) or in advance of your renewal
              (current customers). You are responsible for paying any
              applicable taxes which aren&rsquo;t included in the price and
              will appear on your invoice.
            </p>
          </Section>

          <Section title="5. Term and Termination">
            <p>
              <strong>Subscription Term.</strong> Provider will provide Services
              for the term indicated by start and stop dates on the Order
              Confirmation. If this Agreement is terminated, all rights of use
              expire.
            </p>
            <p>
              <strong>Renewal.</strong> To make Renewal as easy as possible for
              customers, this Agreement includes an automatic renewal. Unless
              Customer cancels its next term at least 30 days before the end of
              the term, this Agreement will automatically renew for the same
              subscription length at Provider&rsquo;s then standard price. If
              Provider does not receive Customer&rsquo;s written cancellation
              at least 30 days before the end of the term, Customer agrees to
              the renewal.
            </p>
          </Section>

          <Section title="6. Confidentiality">
            <p>
              <strong>Non-disclosure.</strong> The Provider and Licensee each
              agree to retain in confidence any non-public information and
              know-how disclosed pursuant to this Agreement (the
              &ldquo;Confidential Information&rdquo;). The Provider&rsquo;s
              Confidential Information shall include the terms and conditions
              of this Agreement and all pricing terms provided to Licensee, as
              well as any Licensed Materials.
            </p>
            <p>
              Each party agrees to: (a) preserve and protect the
              confidentiality of the other party&rsquo;s Confidential
              Information; (b) refrain from using the other party&rsquo;s
              Confidential Information except as contemplated in this
              Agreement; (c) not disclose such Confidential Information to any
              third party except to employees, officers, affiliates,
              controlling stockholders, agents, advisors, subcontractors and
              other representatives as is reasonably required in connection
              with the exercise of its rights and obligations under this
              Agreement; and (d) Immediately notify the other Party of any
              unauthorized disclosure or use of any Confidential Information
              and to assist the other party in remedying such unauthorized use
              or disclosure by taking such steps as are reasonably requested.
            </p>
          </Section>

          <Section title="7. Force Majeure">
            <p>
              The Parties are not liable for any failure to fulfill or for any
              delay in fulfilling its obligations where such failure is due to
              causes beyond its reasonable control, including but not limited
              to acts of war, riots, civil commotion, strikes, acts of nature,
              fire, prohibition of imports, currency restrictions,
              interruptions in electricity supply or telecommunications
              including the Internet, or some other unusual event which
              prevents the fulfillment of this Agreement (a &ldquo;Force
              Majeure Event&rdquo;). Any Party whose performance is delayed by
              a Force Majeure Event must notify the other Party by written
              notice as soon as possible. If the fulfillment of the Agreement
              is essentially delayed each Party has the right to terminate the
              Agreement without further notice.
            </p>
          </Section>

          <Section title="8. Governing Law And Dispute Resolution">
            <p>
              This Agreement is governed by and construed in accordance with
              the laws of Cyprus without giving regard to Cypriot principles of
              conflicts of law. Any dispute arising in relation to the
              Agreement should be settled by Cyprus courts and at the venue of
              the Provider.
            </p>
          </Section>
        </article>
      </div>
    </div>
  );
}
